import React, { useCallback, useMemo, useState } from "react";
import { useTranslate } from "helpers";
import { getTireManufacturersAPI } from "services/api/calculations/tire/api";
import { TireManufacturerRow } from "services/api/calculations/tire/types";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { DropdownAutocomplete } from "../DropdownAutocomplete";
import { DropdownOption } from "../Dropdown/types";
import { TireManufacturerDropdownProps } from "./types";
import { DropdownCacheKey, buildDropdownCacheKey, useDropdownCache } from "services/hooks/useDropdownCache";

const FIVE_MINUTES = 5 * 60 * 1000;

const TireManufacturerDropdown: React.FC<TireManufacturerDropdownProps> = (props) => {
  const {
    name, disabled, required, label, error, value,
    direction, width, height, placeholder, cssClassName, onChange,
  } = props;

  const { t } = useTranslate();

  const [searchValue, setSearchValue] = useState<string>("");

  const { data, isLoading } = useDropdownCache<DataWithPagination<TireManufacturerRow>>(
    buildDropdownCacheKey(DropdownCacheKey.TireManufacturers, searchValue),
    () => getTireManufacturersAPI(initialDataWithPagination.pagination, searchValue),
    FIVE_MINUTES
  );

  const searchResults = data ?? initialDataWithPagination;

  const handleSearchFocus = useCallback(() => {
    setSearchValue("");
  }, []);

  const handleSearchChange = useCallback((name: string) => {
    setSearchValue(name);
  }, []);

  const tireManufacturerItems: DropdownOption[] = useMemo(() => {
    const hasPrimary = searchResults.data.some((i) => i.primary);
    return [...searchResults.data]
      .sort((a, b) => {
        if (a.primary !== b.primary) return a.primary ? -1 : 1;
        return (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
      })
      .map((i) => ({
        value: i.id,
        text: i.name,
        image: i.image,
        group: hasPrimary
          ? (i.primary ? t("admin.fleet.tire.manufacturer.group.primary") : t("admin.fleet.tire.manufacturer.group.other"))
          : undefined,
      }));
  }, [searchResults.data, t]);

  return (
    <DropdownAutocomplete
      label={label}
      name={name}
      disabled={disabled}
      required={required}
      placeholder={placeholder}
      value={value}
      direction={direction}
      width={width}
      height={height}
      options={tireManufacturerItems}
      onChange={onChange}
      onSearchChange={handleSearchChange}
      onSearchFocus={handleSearchFocus}
      error={error}
      isLoading={isLoading}
      inputClassName={cssClassName}
    />
  );
};

export default React.memo(TireManufacturerDropdown);
